import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import {
  InsightsEmptyState,
  InsightsErrorState,
  InsightsTableSkeleton,
} from "../../components/Insights/InsightsAsyncState";

import BreakdownDialogTitle from "./BreakdownDialogTitle";
import GalleryObservationThumb from "./GalleryObservationThumb";
import ProductImageViewer from "./ProductImageViewer";
import PriceHistoryRecordPanel from "./PriceHistoryRecordPanel";
import {
  formatGalleryBreakdownDialogTitle,
  mapPriceHistoryBreakdownRowsToDisplayItems,
} from "./priceHistoryBreakdownDisplay";
import { withDirectusAssetTransformKey } from "./pricingReportImageUrls";

function buildThumbKeyForItem(item) {
  if (item?.images?.length) {
    return `${item.id}:0`;
  }
  return `${item.id}:none`;
}

function buildGalleryThumbsForItems(items) {
  const thumbs = [];
  (items || []).forEach((item) => {
    if (item.images?.length) {
      item.images.forEach((url, imageIndex) => {
        thumbs.push({
          key: `${item.id}:${imageIndex}`,
          url,
          thumbnailUrl: withDirectusAssetTransformKey(url),
          label: `${item.recordAt}`,
          parentItem: item,
          imageIndex,
          isPlaceholder: false,
        });
      });
      return;
    }

    thumbs.push({
      key: `${item.id}:none`,
      url: null,
      label: `${item.recordAt}`,
      parentItem: item,
      imageIndex: null,
      isPlaceholder: true,
    });
  });
  return thumbs;
}

function PriceHistoryBreakdownGalleryDialog({
  open,
  aggregateRow,
  rows,
  isLoading,
  error,
  onClose,
  focusedRecordId,
  initialPanelMode = "view",
  productFilterChoices,
  retailerChoices,
  provinceChoices,
  unitChoices,
  formatProductOptionLabel,
  isPublishedProductsLoading,
  isLoadingFilterOptions,
  resolveProvinceForApi,
  onSaveRecord,
  onVoidRecord,
}) {
  const theme = useTheme();
  const isMobileViewport = useMediaQuery(theme.breakpoints.down("sm"));
  const [selectedThumbKey, setSelectedThumbKey] = useState("");
  const [pendingEditRecordId, setPendingEditRecordId] = useState(null);

  const items = useMemo(() => mapPriceHistoryBreakdownRowsToDisplayItems(rows), [rows]);

  const galleryDialogTitle = useMemo(
    () => formatGalleryBreakdownDialogTitle(aggregateRow),
    [aggregateRow]
  );

  const galleryThumbs = useMemo(() => buildGalleryThumbsForItems(items), [items]);

  useEffect(() => {
    if (!open) return;

    setPendingEditRecordId(focusedRecordId || null);

    if (focusedRecordId) {
      const focusedItem = items.find((item) => item.id === focusedRecordId);
      if (focusedItem) {
        setSelectedThumbKey(buildThumbKeyForItem(focusedItem));
        return;
      }
    }

    setSelectedThumbKey((currentThumbKey) => {
      if (currentThumbKey && galleryThumbs.some((thumb) => thumb.key === currentThumbKey)) {
        return currentThumbKey;
      }
      return galleryThumbs[0]?.key ?? "";
    });
  }, [open, aggregateRow, focusedRecordId, items, galleryThumbs]);

  const selectedThumb = useMemo(
    () => galleryThumbs.find((thumb) => thumb.key === selectedThumbKey) ?? null,
    [galleryThumbs, selectedThumbKey]
  );

  const metadataItem = useMemo(() => {
    if (selectedThumb?.parentItem) {
      return selectedThumb.parentItem;
    }
    return items[0] ?? null;
  }, [selectedThumb, items]);

  const panelInitialMode =
    metadataItem?.id && metadataItem.id === pendingEditRecordId ? initialPanelMode : "view";

  const handleThumbSelect = (thumbKey) => {
    setPendingEditRecordId(null);
    setSelectedThumbKey(thumbKey);
  };

  const viewerImageUrl = selectedThumb?.url ?? "";
  const viewerAlt = metadataItem
    ? `${metadataItem.product} image ${(selectedThumb?.imageIndex ?? 0) + 1}`
    : "Breakdown image";

  const shouldShowThumbnailRow = galleryThumbs.length > 1;

  return (
    <Dialog
      className="insights-dialog"
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      fullScreen={isMobileViewport}
    >
      <BreakdownDialogTitle title={galleryDialogTitle} onClose={onClose} />
      <DialogContent dividers>
        <Stack spacing={2}>
          {error ? (
            <InsightsErrorState
              message={error}
              fallbackMessage="Something went wrong while loading breakdown rows."
            />
          ) : null}

          {isLoading ? (
            <InsightsTableSkeleton />
          ) : items.length === 0 ? (
            <InsightsEmptyState message="No rows found for this group." />
          ) : (
            <Stack direction="column" spacing={2} sx={{ width: "100%" }}>
              <Box sx={{ minWidth: 0, width: "100%" }}>
                {viewerImageUrl ? (
                  <ProductImageViewer
                    src={viewerImageUrl}
                    alt={viewerAlt}
                    minHeight={400}
                    maxHeight={520}
                  />
                ) : (
                  <Box
                    sx={{
                      width: "100%",
                      minHeight: 400,
                      borderRadius: 1,
                      border: "1px solid #333",
                      backgroundColor: "#000",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="body2" color="grey.400">
                      No image for this record
                    </Typography>
                  </Box>
                )}
              </Box>

              {shouldShowThumbnailRow ? (
                <Stack spacing={1}>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      flexWrap: "nowrap",
                      overflowX: "auto",
                      gap: 1,
                      pb: 1,
                      scrollSnapType: "x mandatory",
                    }}
                    role="list"
                    aria-label="Breakdown images"
                  >
                    {galleryThumbs.map((thumb) => (
                      <GalleryObservationThumb
                        key={thumb.key}
                        thumb={thumb}
                        isSelected={selectedThumbKey === thumb.key}
                        onSelect={handleThumbSelect}
                      />
                    ))}
                  </Stack>
                </Stack>
              ) : null}

              <Box sx={{ minWidth: 0, width: "100%" }}>
                <PriceHistoryRecordPanel
                  item={metadataItem}
                  initialPanelMode={panelInitialMode}
                  productFilterChoices={productFilterChoices}
                  retailerChoices={retailerChoices}
                  provinceChoices={provinceChoices}
                  unitChoices={unitChoices}
                  formatProductOptionLabel={formatProductOptionLabel}
                  isPublishedProductsLoading={isPublishedProductsLoading}
                  isLoadingFilterOptions={isLoadingFilterOptions}
                  resolveProvinceForApi={resolveProvinceForApi}
                  onSaveRecord={onSaveRecord}
                  onVoidRecord={onVoidRecord}
                />
              </Box>
            </Stack>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

export default PriceHistoryBreakdownGalleryDialog;
